import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import type { GlossaryEntry } from '@/data/glossary.schema'
import glossaryIT from '@/data/glossary.it'
import glossaryEN from '@/data/glossary.en'
import Icon from '@/components/ui/Icon'
import { IcBookOpen } from '@/components/ui/icons'

interface Props {
  termId: string | null
  onClose: () => void
  onNavigate?: (termId: string) => void
}

export default function EducationalDrawer({ termId, onClose, onNavigate }: Props) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'en' ? 'en' : 'it'
  const glossary = lang === 'en' ? glossaryEN : glossaryIT
  const entry: GlossaryEntry | undefined = termId ? glossary[termId] : undefined
  const drawerRef = useRef<HTMLDivElement>(null)

  const CATEGORY_LABELS: Record<string, string> = {
    algorithm:  t('glossary.categoryAlgorithm', 'Algoritmo'),
    physiology: t('glossary.categoryPhysiology', 'Fisiologia'),
    gas:        t('glossary.categoryGas', 'Gas'),
    safety:     t('glossary.categorySafety', 'Sicurezza'),
    physics:    t('glossary.categoryPhysics', 'Fisica'),
    unit:       t('glossary.categoryUnit', 'Unità'),
  }

  const LEVEL_LABELS: Record<string, { label: string; color: string }> = {
    basic:        { label: t('glossary.levelBasic', 'Base'),         color: 'bg-emerald-800 text-emerald-200' },
    intermediate: { label: t('glossary.levelIntermediate', 'Intermedio'), color: 'bg-sky-800 text-sky-200' },
    advanced:     { label: t('glossary.levelAdvanced', 'Avanzato'),  color: 'bg-purple-800 text-purple-200' },
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  useEffect(() => {
    if (termId) drawerRef.current?.focus()
  }, [termId])

  if (!termId) return null

  const catLabel = entry ? (CATEGORY_LABELS[entry.category] ?? entry.category) : ''
  const lvl = entry ? LEVEL_LABELS[entry.level] : null

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" role="dialog" aria-modal="true" aria-label={entry?.term}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        tabIndex={-1}
        className="relative h-full w-full max-w-[440px] bg-ocean-950 border-l border-ocean-700 flex flex-col shadow-2xl overflow-y-auto outline-none"
        style={{ animation: 'slideInRight 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ocean-700 sticky top-0 bg-ocean-950 z-10">
          <div className="flex items-center gap-2">
            <span className="text-lg"><Icon icon={IcBookOpen} size="md" /></span>
            <span className="text-sm font-semibold text-slate-200">
              {lang === 'en' ? 'Glossary' : 'Glossario'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-ocean-800 text-slate-400 hover:text-white transition-colors"
            aria-label={t('auto.componentsEducationalEducationalDrawer.close')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {entry ? (
          <div className="px-5 py-5 space-y-5 flex-1">
            {/* Term + badges */}
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">{entry.term}</h2>
              {entry.synonyms?.length ? (
                <p className="text-xs text-slate-500 mt-0.5">
                  {lang === 'en' ? 'Also: ' : 'Anche: '}{entry.synonyms.join(', ')}
                </p>
              ) : null}
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-ocean-700 text-slate-300">
                  {catLabel}
                </span>
                {lvl && (
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${lvl.color}`}>
                    {lvl.label}
                  </span>
                )}
              </div>
            </div>

            <div className="border-t border-ocean-700/60" />

            {/* Full explanation */}
            <div className="prose prose-sm prose-invert max-w-none text-slate-300 leading-relaxed">
              <ReactMarkdown>{entry.full_explanation}</ReactMarkdown>
            </div>

            {/* Worked example */}
            {entry.worked_example && (
              <div className="rounded-xl bg-ocean-800/60 border border-ocean-600/40 p-4">
                <div className="text-xs font-semibold text-amber-400 mb-2">
                  {lang === 'en' ? '🔍 Worked example' : '🔍 Esempio pratico'}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{entry.worked_example}</p>
              </div>
            )}

            {/* Related terms */}
            {entry.related_terms?.length ? (
              <div>
                <div className="text-xs font-semibold text-slate-400 mb-2">
                  {lang === 'en' ? 'Related terms' : 'Termini correlati'}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {entry.related_terms.map(id => {
                    const rel = glossary[id]
                    if (!rel) return null
                    return (
                      <button
                        key={id}
                        onClick={() => onNavigate?.(id)}
                        className="text-xs px-2.5 py-1 rounded-full bg-ocean-800 border border-ocean-600 text-sky-300 hover:bg-ocean-700 transition-colors"
                      >
                        {rel.term}
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {/* References */}
            {entry.references?.length ? (
              <div>
                <div className="text-xs font-semibold text-slate-400 mb-2">
                  {lang === 'en' ? 'References' : 'Riferimenti'}
                </div>
                <ul className="space-y-1">
                  {entry.references.map((ref, i) => (
                    <li key={i} className="text-xs text-slate-500">
                      {ref.url ? (
                        <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">
                          {ref.title}
                        </a>
                      ) : (
                        <span className="text-slate-400">{ref.title}</span>
                      )}
                      {ref.citation && <span> — {ref.citation}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
            {lang === 'en' ? 'Term not found.' : 'Termine non trovato.'}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ocean-700 sticky bottom-0 bg-ocean-950">
          <a
            href="/app/glossary"
            className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
            onClick={onClose}
          >
            {lang === 'en' ? 'Open full glossary →' : 'Apri il glossario completo →'}
          </a>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  )
}
